'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { socketService } from '@/lib/services/socket';
import { logger } from '@/lib/monitoring/logger';
import type { Socket } from 'socket.io-client';
import type { EventMap } from '@/lib/services/socket';
import type { SocketResponse, Tournament } from '@/lib/types/auction';
import { useAuth } from '@/hooks/useAuth';
import { handleError } from '@/lib/utils/error';

interface SocketContextType {
  socket: Socket | null;
  subscribe: <K extends keyof EventMap>(
    event: K,
    callback: (data: SocketResponse<EventMap[K]>) => void
  ) => void;
  unsubscribe: <K extends keyof EventMap>(
    event: K,
    callback: (data: SocketResponse<EventMap[K]>) => void
  ) => void;
}

// Create a no-op context for when socket is not connected
const defaultContext: SocketContextType = {
  socket: null,
  subscribe: () => {},
  unsubscribe: () => {},
};

const SocketContext = createContext<SocketContextType>(defaultContext);

interface SocketProviderProps {
  tournamentId: string;
  children: React.ReactNode;
  tournament?: Tournament;
}

export function SocketProvider({
  tournamentId,
  children,
  tournament,
}: SocketProviderProps) {
  const { token, isAuthenticated, isInitialized } = useAuth();
  const [shouldConnect, setShouldConnect] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    // Wait for auth to initialize before making connection decisions
    if (!isInitialized) {
      setShouldConnect(false);
      return;
    }

    // Only connect if tournament exists and is in ACTIVE state
    if (!tournamentId || !tournament) {
      logger.info('Socket connection skipped - missing tournament data', {
        tournamentId,
        hasTournament: !!tournament,
      });
      setShouldConnect(false);
      return;
    }

    if (tournament.status !== 'ACTIVE') {
      logger.info('Socket connection skipped - tournament not active', {
        tournamentId,
        status: tournament.status,
        message: 'Socket connections are only allowed for ACTIVE tournaments',
      });
      setShouldConnect(false);
      // Ensure any existing connection is cleaned up
      if (socketService.getSocketInstance()?.connected) {
        logger.info(
          'Cleaning up existing socket connection for non-active tournament'
        );
        socketService.cleanup();
      }
      return;
    }

    // Allow connection for both authenticated users and public viewers for ACTIVE tournaments
    logger.info('Socket connection approved', {
      tournamentId,
      status: tournament.status,
      isAuthenticated,
      isPublicViewer: !isAuthenticated && !token,
      isInitialized,
    });
    setShouldConnect(true);
  }, [token, tournamentId, tournament, isAuthenticated, isInitialized]);

  useEffect(() => {
    if (!shouldConnect) {
      logger.info('Cleaning up socket - should not connect', {
        tournamentId,
        tournamentStatus: tournament?.status,
      });
      socketService.cleanup();
      setIsConnecting(false);
      setSocket(null);
      return;
    }

    let retryTimeout: NodeJS.Timeout;
    let mounted = true;

    const initializeSocket = async () => {
      if (isConnecting || socketService.getSocketInstance()?.connected) return;

      setIsConnecting(true);
      try {
        logger.info('🔌 Connecting to socket for ACTIVE tournament', {
          tournamentId,
          status: tournament?.status,
        });

        await socketService.connect(token || null, tournamentId);

        if (mounted) {
          setIsConnecting(false);
          setSocket(socketService.getSocketInstance());
          logger.info('✅ Socket connected successfully', { tournamentId });
        }
      } catch (err) {
        handleError(err, 'SocketProvider.connect');
        if (mounted) {
          setIsConnecting(false);
          // Only retry if tournament is still active
          if (tournament?.status === 'ACTIVE') {
            retryTimeout = setTimeout(initializeSocket, 2000);
          }
        }
      }
    };

    initializeSocket();

    return () => {
      mounted = false;
      clearTimeout(retryTimeout);
      logger.info('🧹 Cleaning up socket connection', {
        tournamentId,
        reason: 'Component unmount or dependency change',
      });
      try {
        socketService.cleanup();
        setIsConnecting(false);
        setSocket(null);
      } catch (err) {
        handleError(err, 'SocketProvider.cleanup');
      }
    };
  }, [shouldConnect, token, tournamentId, tournament, isAuthenticated]);

  const contextValue: SocketContextType =
    shouldConnect && socket
      ? {
          socket: socketService.getSocketInstance(),
          subscribe: socketService.on.bind(socketService),
          unsubscribe: socketService.off.bind(socketService),
        }
      : defaultContext;

  // Show connecting state only for ACTIVE tournaments when auth is initialized
  if (shouldConnect && !socket && isConnecting && isInitialized) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">Connecting to auction server...</p>
      </div>
    );
  }

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocketContext() {
  const context = useContext(SocketContext);
  if (!context) {
    logger.warn('Socket context not available');
    throw new Error('❌ useSocketContext must be used within a SocketProvider');
  }
  return context;
}
